import React from 'react';
import { WhatsAppIcon, MessengerIcon, InstagramIcon, WebsiteIcon, ShopifyIcon } from './BrandIcons';
import { cn } from '@/lib/utils';

/**
 * Single source of truth for platform → icon + channel-token color across
 * the Conversations page (list rows, chat header, avatar corner badges).
 * Keys match the `platform` string on the mock/API Conversation shape.
 */
const PLATFORM_META: Record<
  string,
  {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    text: string;
    bg: string;
    fg: string;
  }
> = {
  facebook: {
    icon: MessengerIcon,
    label: 'Messenger',
    text: 'text-[#0084FF] dark:text-[#0084FF]',
    bg: 'bg-[#0084FF]',
    fg: 'text-white',
  },
  instagram: {
    icon: InstagramIcon,
    label: 'Instagram',
    text: 'text-[#E4405F] dark:text-[#E4405F]',
    bg: 'bg-gradient-to-tr from-[#F58529] via-[#DD2A7B] to-[#8134AF]',
    fg: 'text-white',
  },
  whatsapp: {
    icon: WhatsAppIcon,
    label: 'WhatsApp',
    text: 'text-[#25D366] dark:text-[#25D366]',
    bg: 'bg-[#25D366]',
    fg: 'text-white',
  },
  website: {
    icon: WebsiteIcon,
    label: 'Website',
    text: 'text-indigo-600 dark:text-indigo-400',
    bg: 'bg-indigo-600',
    fg: 'text-white',
  },
  shopify: {
    icon: ShopifyIcon,
    label: 'Shopify',
    text: 'text-[#96BF48] dark:text-[#96BF48]',
    bg: 'bg-[#96BF48]',
    fg: 'text-white',
  },
};

const FALLBACK = PLATFORM_META.website;

export const getPlatformMeta = (platform: string) => PLATFORM_META[platform] ?? FALLBACK;

/** Bare icon, tinted with the channel token — for inline use (chat header). */
export const PlatformIcon = ({ platform, className }: { platform: string; className?: string }) => {
  const meta = getPlatformMeta(platform);
  const Icon = meta.icon;
  return <Icon className={cn('h-3.5 w-3.5', meta.text, className)} />;
};

/** Small filled circle badge meant to sit on an avatar's corner. */
export const PlatformBadge = ({ platform, className }: { platform: string; className?: string }) => {
  const meta = getPlatformMeta(platform);
  const Icon = meta.icon;
  return (
    <span
      className={cn(
        'flex h-4 w-4 items-center justify-center rounded-full ring-2 ring-white dark:ring-slate-900',
        meta.bg,
        className
      )}
    >
      <Icon className={cn('h-2.5 w-2.5', meta.fg)} />
    </span>
  );
};
